import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PRODUCTS, SOLUTIONS_BY_INDUSTRY, SOLUTIONS_BY_CAPABILITY, ProductItem, SolutionItem } from "../data/productsData";
import { CERTIFICATIONS, CERTIFICATION_NOTE, Certification } from "../data/certificationsData";
import { loadOffices, loadCertifications, loadSolutions, OfficeLocation } from "../services/platformSource";
import { loadProducts } from "../services/productSource";
import "../styles/revamp.css";

// Rendered before the CMS answers, and whenever it cannot be reached.
const FOOTER_FALLBACK: OfficeLocation[] = [
  { name: "India", flag: "/assets/imgs/flags/ind_img.svg", address: "A/1109, Money Plant High Street, Jagatpur Road, Near S.G. Highway, Ahmedabad - 382470", phone: "+91 9429167971" },
  { name: "USA", flag: "/assets/imgs/flags/usa_img.svg", address: "400 Capital Circle SE, Suite 18281, Tallahassee, FL 32301. USA", phone: "" },
  { name: "Australia", flag: "/assets/imgs/flags/aus_img.svg", address: "1 King Street, Sydney, NSW 2000, Australia", phone: "" },
  { name: "UAE", flag: "/assets/imgs/flags/uae_img.svg", address: "Silicon Oasis, Dubai, UAE", phone: "" },
  { name: "UK", flag: "/assets/imgs/flags/uk_img.svg", address: "221B Baker Street, London, UK", phone: "" },
];

const Footer = () => {
  // Seeded from the bundled defaults so the footer renders on first paint, then
  // replaced by the CMS list — opening or closing an office is now a content
  // change rather than a deploy.
  const [locations, setLocations] = useState<OfficeLocation[]>(FOOTER_FALLBACK);
  const [certifications, setCertifications] = useState<Certification[]>(CERTIFICATIONS);
  const [activeLocation, setActiveLocation] = useState<OfficeLocation>(FOOTER_FALLBACK[0]);
  const [footerProducts, setFooterProducts] = useState<ProductItem[]>(PRODUCTS);
  const [footerSolutions, setFooterSolutions] = useState<{ industry: SolutionItem[]; capability: SolutionItem[] }>({
    industry: SOLUTIONS_BY_INDUSTRY,
    capability: SOLUTIONS_BY_CAPABILITY,
  });

  useEffect(() => {
    let cancelled = false;
    loadCertifications().then((rows) => {
      if (!cancelled) setCertifications(rows);
    });
    loadOffices().then((rows) => {
      if (cancelled || rows.length === 0) return;
      setLocations(rows);
      setActiveLocation((current) => rows.find((r) => r.name === current.name) ?? rows[0]);
    });
    loadProducts().then((rows) => {
      if (!cancelled && rows.length > 0) setFooterProducts(rows);
    });
    loadSolutions().then((res) => {
      if (!cancelled) setFooterSolutions(res);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <>
      <footer className="footer-area">
        <div className="container">
          <div className="footer-area-inner">
            <div className="shape-1" style={{ maxWidth: "350px", zIndex: 0, pointerEvents: "none" }}>
              <img
                className="show-light"
                src="/assets/imgs/shape/img-s-33.webp"
                alt="shape"
                style={{ width: "100%", height: "auto" }} loading="lazy" decoding="async" />
              <img
                className="show-dark"
                src="/assets/imgs/shape/img-s-33-light.webp"
                alt="shape"
                style={{ width: "100%", height: "auto" }} loading="lazy" decoding="async" />
            </div>

            {/* Five columns, modelled on how a B2B platform footer is expected
                to work: a visitor who scrolled past the header still finds every
                product, both ways into solutions, and the company links. The
                previous version was a single Platform list against an empty
                half-width. */}
            <div className="mx-footer-grid">
              <div className="mx-footer-col">
                <h2 className="mx-footer-heading">Platform</h2>
                <ul className="mx-footer-links">
                  {footerProducts.map((p) => (
                    <li key={p.slug} style={{ ["--mx-dot-color" as any]: p.accent }}>
                      <Link to={`/products/${p.slug}`}>
                        <span className="mx-dot" />
                        {p.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mx-footer-col">
                <h2 className="mx-footer-heading">Solutions</h2>
                <p className="mx-footer-subheading">By industry</p>
                <ul className="mx-footer-links">
                  {footerSolutions.industry.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/products?solution=${s.slug}`}>{s.title}</Link>
                    </li>
                  ))}
                </ul>
                <p className="mx-footer-subheading">By capability</p>
                <ul className="mx-footer-links">
                  {footerSolutions.capability.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/products?solution=${s.slug}`}>{s.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mx-footer-col">
                <h2 className="mx-footer-heading">Resources</h2>
                <ul className="mx-footer-links">
                  <li><Link to="/case-studies">Case studies</Link></li>
                  <li><Link to="/blogs">Blog</Link></li>
                  <li><Link to="/faqs">FAQs</Link></li>
                  <li><Link to="/security">Security</Link></li>
                </ul>
              </div>

              <div className="mx-footer-col">
                <h2 className="mx-footer-heading">Company</h2>
                <ul className="mx-footer-links">
                  <li><Link to="/about">Why MindSyncX</Link></li>
                  <li><Link to="/services">Services</Link></li>
                  <li><Link to="/teams">Team</Link></li>
                  <li><Link to="/career">Careers</Link></li>
                  <li><Link to="/contact">Contact</Link></li>
                </ul>
              </div>

              <div className="mx-footer-col">
                <h2 className="mx-footer-heading">{activeLocation.name}</h2>
                <p className="mx-footer-address">{activeLocation.address}</p>
                {activeLocation.phone && (
                  <p className="mx-footer-address">{activeLocation.phone}</p>
                )}
                <div className="mx-footer-flags">
                  {locations.map((loc, index) => (
                    <button
                      key={index}
                      type="button"
                      className="mx-flag"
                      onMouseEnter={() => setActiveLocation(loc)}
                      onFocus={() => setActiveLocation(loc)}
                      onClick={() => setActiveLocation(loc)}
                      aria-label={`Show the ${loc.name} office address`}
                      aria-pressed={activeLocation.name === loc.name}
                    >
                      <img
                        src={loc.flag}
                        alt={loc.name}
                        title={loc.name}
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

              {/* Certifications sit above the legal bar because that is where a
                  procurement reader looks for them. Each links to its own
                  certificate and to the IAF register, so the claim is checkable
                  rather than a logo. */}
              <div className="mx-certs">
                <p className="mx-eyebrow">Certified</p>
                <ul className="mx-cert-list">
                  {certifications.map((cert) => (
                    <li key={cert.standard}>
                      <a href={cert.documentUrl} target="_blank" rel="noopener noreferrer">
                        <strong>{cert.standard}</strong>
                        <span>{cert.covers}</span>
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="mx-cert-note">
                  {CERTIFICATION_NOTE}{" "}
                  <a
                    href="https://www.iafcertsearch.org"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Verify on the IAF register ↗
                  </a>
                </p>
              </div>
            </div>
          </div>

        <div className="copyright-area">
          <div className="container">
            <div className="copyright-area-inner">
              <div className="copyright-text">
                <p className="text">
                  © 2025 | All rights reserved.{" "}
                  <a
                    href="/"
                    rel="noreferrer"
                  >
                    MindSyncX Labs LLP
                  </a>
                </p>
              </div>
              <ul className="footer-nav-list">
                <li>
                  <a href="/about">About Us</a>
                </li>
                <li>
                  <a href="/security">Security</a>
                </li>
                <li>
                  <a href="/privacy-policy">Privacy Policy</a>
                </li>
                <li>
                  <a href="/terms-conditions">Terms Conditions</a>
                </li>
                {/* <li>
                  <a href="/career">Career</a>
                </li> */}
              </ul>
              <ul className="social-links">
                <li>
                  <a href="https://www.youtube.com/@MindSyncX" target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-youtube"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.facebook.com/profile.php?id=61577047823875&sk=about" target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-facebook-f"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.x.com/mindsyncx" target="_blank" rel="noopener noreferrer">
                    𝕏
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/mindsyncx_labs" target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-instagram"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/company/mindsyncxlabs" target="_blank" rel="noopener noreferrer">
                    <i className="fa-brands fa-linkedin"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
